import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { FRAMEWORK_BY_ID, type FrameworkId } from '@/constants/frameworks.ts';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { cn } from '@/utils/classNames.ts';

type FrameworkAttributionChipProps = PropsWithClassName<{
  frameworkId: string;
}>;

/** Recommendation / governance review only — not on loader or commitment steps. */
const FrameworkAttributionChip: FC<FrameworkAttributionChipProps> = ({ frameworkId, className }) => {
  const { t } = useTranslation();
  const meta = FRAMEWORK_BY_ID[frameworkId as FrameworkId];
  const frameworkLabel = meta?.label ?? frameworkId;
  const tooltipText =
    meta?.description ??
    t('recommendationEnginePage.governanceReview.frameworkAttributionTooltipFallback');

  return (
    <div
      className={cn(
        'tooltip w-full cursor-default before:max-w-[260px] before:text-left before:text-xs before:font-dm',
        className,
      )}
      data-tip={tooltipText}
    >
      <div
        className="inline-flex w-full flex-row items-center gap-2 rounded-xl bg-[#E8F5E9] px-4 py-2.5"
        role="status"
        aria-label={t('recommendationEnginePage.governanceReview.frameworkApplied', {
          framework: frameworkLabel,
        })}
      >
        <InformationCircleIcon className="w-5 h-5 text-[#2E7D32] shrink-0" aria-hidden />
        <span className="font-dm text-sm font-semibold text-[#2E7D32]">
          {t('recommendationEnginePage.governanceReview.frameworkApplied', {
            framework: frameworkLabel,
          })}
        </span>
      </div>
    </div>
  );
};

export default FrameworkAttributionChip;
